// Abspielmodus einer Video-Ebene — EINE Quelle der Wahrheit.
//
// Historisch beschreiben DREI Felder denselben Sachverhalt:
//
//   loop         boolean   — Jans urspruengliches Haekchen im Pro-Editor
//   scrollDriven boolean   — sein zweites Haekchen ("Beim Scrollen abspielen")
//   playbackMode enum      — das spaetere Feld aus dem Template-Editor
//
// Der Renderer wertet sie mit einer festen Rangfolge aus (scrollDriven >
// playbackMode > loop). Solange die Bedienoberflaeche eine ANDERE Rangfolge
// annimmt als der Renderer, luegt sie: Genau das war Jans Fund 03.08.2026 —
// ein Template-Video traegt playbackMode:'loop', im Pro-Editor nimmt man das
// Loop-Haekchen raus, und im Reader passiert nichts, weil playbackMode das
// Haekchen ueberstimmt.
//
// Deshalb liegen Lesen UND Schreiben hier zusammen: Menue und Renderer
// benutzen dieselben zwei Funktionen, sie koennen nicht auseinanderlaufen.

export type VideoPlaybackMode = "loop" | "pingpong" | "once" | "scroll";

/** Alle Felder, aus denen der Modus abgeleitet wird (bewusst lose typisiert —
 *  aeltere Szenen tragen nicht alle drei). */
export interface VideoPlaybackFields {
    loop?: boolean;
    scrollDriven?: boolean;
    playbackMode?: VideoPlaybackMode;
}

/**
 * Rangfolge, exakt wie der Renderer sie anwendet:
 *   scrollDriven ueberstimmt alles → dann playbackMode → dann das alte
 *   loop-Haekchen (loop:false heisst "einmal abspielen").
 *
 * Wichtig fuer Alt-Szenen: ein Pro-Editor-Video ohne playbackMode und ohne
 * gesetztes loop gilt weiterhin als 'loop' (bisheriges Verhalten).
 */
export function resolveVideoPlaybackMode(
    content: VideoPlaybackFields | null | undefined,
): VideoPlaybackMode {
    if (!content) return "loop";
    if (content.scrollDriven) return "scroll";
    if (content.playbackMode) return content.playbackMode;
    return content.loop === false ? "once" : "loop";
}

/**
 * Gegenstueck zum Lesen: setzt ALLE drei Felder passend zum gewaehlten Modus.
 *
 * Nur eins davon zu schreiben ist der Fehler, den es hier zu verhindern gilt —
 * ein zurueckgebliebenes playbackMode oder scrollDriven ueberstimmt sonst still
 * die neue Wahl. Auch der Template-Editor haelt playbackMode und loop synchron
 * (updateVideoPlaybackMode in app/src/lib/template-utils.ts), damit beide
 * Editoren dasselbe Video gleich verstehen.
 */
/**
 * Welche Modi darf man ueberhaupt AUSWAEHLEN?
 *
 * Ping-Pong ist in beiden Editoren raus (Jans Entscheidung 03.08.2026: "Das
 * Video stockt total ... das laeuft nicht fluessig", erst fuer den Pro-Editor,
 * nach dem Gegenpruefen auch fuer den Template-Editor). Der Grund ist
 * bauartbedingt und nicht wegzuoptimieren: Browser spielen nicht rueckwaerts ab
 * (keine negative playbackRate), der Renderer faehrt die Position deshalb ueber
 * Sprungmarken — gemessen ~20 Bilder/s bei dauerhaftem Seek und ohne Ton.
 *
 * Der Renderer KANN den Modus weiterhin, und bestehende Scrollys tragen ihn
 * womoeglich. Deshalb die Ausnahme: steht ein Video gerade auf Ping-Pong, bleibt
 * der Eintrag sichtbar — sonst zeigte der Waehler ein leeres Feld bzw. keine
 * aktive Schaltflaeche, und das Video liesse sich nicht mehr umstellen. Beim
 * Wegschalten verschwindet er.
 *
 * Die Reihenfolge ist die Anzeigereihenfolge.
 */
export function offeredVideoPlaybackModes(
    aktuell: VideoPlaybackMode,
): VideoPlaybackMode[] {
    const modi: VideoPlaybackMode[] = ["loop", "once", "scroll"];
    if (aktuell === "pingpong") modi.splice(1, 0, "pingpong");
    return modi;
}

export function videoPlaybackModePatch(mode: VideoPlaybackMode): {
    playbackMode: VideoPlaybackMode;
    loop: boolean;
    scrollDriven: boolean;
} {
    return {
        playbackMode: mode,
        loop: mode === "loop",
        scrollDriven: mode === "scroll",
    };
}
